import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Tag, Image, Badge, Tooltip } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { getAllCategory } from "../../../api/AdminRequest";
import EditCategoryModal from "./EditCategoryModal";
import EditSubCategoryModal from "./EditSubCategoryModal";

function CategoryList() {
  const navigate = useNavigate();
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit modal states
  const [editCategoryModalVisible, setEditCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editSubCategoryModalVisible, setEditSubCategoryModalVisible] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await getAllCategory();
      if (data?.success) {
        console.log("data", data);
        setCategoryData(data?.categoryData || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubcategory = (record) => {
    navigate("/create-category", { state: { categoryId: record._id, categoryName: record.name } });
    console.log("categoryId", record._id)
  };

  // Navigate to create category page
  const handleAddCategory = () => {
    navigate("/create-category");
  };

  // Edit category handler
  const handleEditCategory = (record) => {
    setSelectedCategory(record);
    setEditCategoryModalVisible(true);
  };

  // Edit subcategory handler
  const handleEditSubCategory = (subcategory) => {
    setSelectedSubCategory(subcategory);
    setEditSubCategoryModalVisible(true);
  };

  // Main table columns
  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 100,
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Category"
          width={50}
          height={50}
          style={{ borderRadius: 8, objectFit: "cover" }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAAA=="
        />
      ),
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="font-medium text-gray-800">{name}</span>,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (value) => (
        <code className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-600">
          {value}
        </code>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span>
          <Tag className="w-16 flex justify-center" color={status === "active" ? "green" : "gold"}>
            {status}
          </Tag>
        </span>
      ),
    },
    {
      title: "Subcategories",
      dataIndex: "subCategories",
      key: "subCategories",
      render: (subCategories) => (
        <Badge
          count={subCategories?.length || 0}
          style={{ backgroundColor: "#1955EB" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Tooltip title="Edit Category">
            <button
              type="button"
              onClick={() => handleEditCategory(record)}
              className="w-8 h-8 flex items-center justify-center bg-blue-50 text-[#1955EB] rounded-lg hover:bg-[#1955EB] hover:text-white transition-all shadow-sm border border-blue-100"
            >
              <EditOutlined style={{ fontSize: 16 }} />
            </button>
          </Tooltip>
          <Tooltip title="Add Subcategory">
            <button
              type="button"
              onClick={() => handleAddSubcategory(record)}
              className="w-8 h-8 flex items-center justify-center bg-blue-50 text-[#1955EB] rounded-lg hover:bg-[#1955EB] hover:text-white transition-all shadow-sm border border-blue-100"
            >
              <PlusOutlined style={{ fontSize: 16 }} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  // Subcategory columns
  const subCategoryColumns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 80,
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Subcategory"
          width={40}
          height={40}
          style={{ borderRadius: 6, objectFit: "cover" }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHsAAA="
        />
      ),
    },
    {
      title: "Subcategory Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (value) => (
        <code className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-600">
          {value}
        </code>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span>
          <Tag className="w-16 flex justify-center" color={status === "active" ? "green" : "gold"}>
            {status}
          </Tag>
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Tooltip title="Edit Subcategory">
            <button
              type="button"
              onClick={() => handleEditSubCategory(record)}
              className="w-8 h-8 flex items-center justify-center bg-blue-50 text-[#1955EB] rounded-lg hover:bg-[#1955EB] hover:text-white transition-all shadow-sm border border-blue-100"
            >
              <EditOutlined style={{ fontSize: 16 }} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  // Expanded row render for subcategories
  const expandedRowRender = (record) => {
    const subCategories = record.subCategories || [];

    if (subCategories.length === 0) {
      return (
        <p className="text-center text-gray-400 italic py-4">
          No subcategories available
        </p>
      );
    }

    return (
      <Table
        columns={subCategoryColumns}
        dataSource={subCategories.map((sub, index) => ({
          ...sub,
          key: sub._id || index,
        }))}
        pagination={false}
        size="small"
        className="custom-table-header border border-gray-100 rounded-xl"
      />
    );
  };

  return (
    <div className="flex-grow overflow-y-auto scrollbar-hide p-4 lg:p-6 md:p-5">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
          <div className="text-left w-full">
            <h2 className="text-2xl font-bold text-gray-800">
              Category Management
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              View and manage all categories and their subcategories
            </p>
          </div>
          <button
            onClick={handleAddCategory}
            className="px-8 py-2 text-[12px] font-medium text-white bg-[#1955EB] hover:border-[#1955EB] border-[1px] rounded-md hover:bg-white hover:text-[#1955EB]"
          >
            + Add Category
          </button>
        </div>

        {/* Category Table */}
        <div className="pt-[0px] space-y-10">
          <Table
            columns={columns}
            dataSource={categoryData.map((cat) => ({
              ...cat,
              key: cat._id,
            }))}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) =>
                record.subCategories && record.subCategories.length > 0,
            }}
            pagination={false}
            size="small"
            className="custom-table-header w-full overflow-x-auto custom-pagination border border-gray-100 rounded-xl"
            rowKey="_id"
            loading={loading}
          />
        </div>
      </div>

      {/* Edit Category Modal */}
      <EditCategoryModal
        visible={editCategoryModalVisible}
        onClose={() => {
          setEditCategoryModalVisible(false);
          setSelectedCategory(null);
        }}
        category={selectedCategory}
        onSuccess={fetchCategories}
      />

      {/* Edit SubCategory Modal */}
      <EditSubCategoryModal
        visible={editSubCategoryModalVisible}
        onClose={() => {
          setEditSubCategoryModalVisible(false);
          setSelectedSubCategory(null);
        }}
        subcategory={selectedSubCategory}
        onSuccess={fetchCategories}
      />
    </div>
  );
}

export default CategoryList;
